import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, startOfDay } from 'date-fns';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { SlotsSlider } from '@/components/calendar/SlotsSlider';
import { ClassRequestModal } from '@/components/classRequests/ClassRequestModal';
import { IndividualRequestCard } from '@/components/classRequests/IndividualRequestCard';

import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { useClassRequestSubmission, useStudentRequests, useApproveClassRequest, useDenyClassRequest, useInstructorRequests } from '@/hooks/useClassRequests';
import { useCentralizedSlots } from '@/hooks/useCentralizedSlots';
import { useInstructorClasses } from '@/hooks/useInstructorClasses';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Slot {
  slot_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  is_available: boolean;
}

interface DaySlot {
  date: Date;
  dayName: string;
  dayNumber: string;
  slotCount: number;
}

export const InstructorSlots: React.FC = () => {
  const { instructorId } = useParams<{ instructorId: string }>();
  const navigate = useNavigate();
  const { t, currentLanguage } = useI18n();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Get date from URL params or default to today
  const dateParam = searchParams.get('date');
  const initialDate = dateParam ? (() => {
    // Parse date in local timezone to avoid UTC conversion issues
    const [year, month, day] = dateParam.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  })() : new Date();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [instructorName, setInstructorName] = useState('');
  const [instructorAvatar, setInstructorAvatar] = useState('');
  const [instructorBeltRank, setInstructorBeltRank] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());

  // Check if user is viewing their own profile (instructor) or someone else's
  const isOwnProfile = instructorId === user?.id;

  // Get instructor classes and requests for centralized slot calculation
  const { classes: instructorClasses = [] } = useInstructorClasses(selectedDate);
  const { data: instructorRequests = [] } = useInstructorRequests(instructorId || '');

  // Use centralized slots hook
  const {
    slots: availabilitySlots,
    loading: agendaLoading,
    agendaConfig: effectiveAgendaConfig
  } = useCentralizedSlots({
    instructorId: instructorId || '',
    selectedDate,
    classes: instructorClasses,
    trainings: instructorClasses // Using classes as trainings for now
  });

  // Convert to expected format
  const slots = availabilitySlots.map((slot, index) => ({
    slot_id: `slot-${index}`,
    start_time: slot.startTime,
    end_time: slot.endTime,
    duration_minutes: slot.standardDuration,
    is_available: true
  }));

  // Generate days for slider with centralized slot counts
  const days = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    
    // Use centralized slots calculation for this specific date
    const { slots } = useCentralizedSlots({
      instructorId: instructorId || '',
      selectedDate: date,
      classes: instructorClasses,
      trainings: instructorClasses // Using classes as trainings for now
    });
    
    return {
      date,
      dayName: format(date, 'EEE').toUpperCase(),
      dayNumber: format(date, 'dd'),
      slotCount: slots.length
    };
  });

  const loading = false; // Simplified
  const normalizeTime = (timeStr: string) => timeStr.split(':').slice(0, 2).join(':');

  // Hooks for class requests
  const queryClient = useQueryClient();
  const submitRequestMutation = useClassRequestSubmission();
  const { data: studentRequests = [] } = useStudentRequests(user?.id || '');
  const approveRequestMutation = useApproveClassRequest();
  const denyRequestMutation = useDenyClassRequest();


  useEffect(() => {
    if (instructorId) {
      loadInstructorData();
    }
  }, [instructorId]);

  const loadInstructorData = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, profile_picture_url, belt_rank')
        .eq('id', instructorId)
        .single();
      
      if (profile) {
        setInstructorName(profile.full_name || 'Instructor');
        setInstructorAvatar(profile.profile_picture_url || '');
        setInstructorBeltRank(profile.belt_rank || 'white');
      }
    } catch (error) {
      console.error('Error loading instructor data:', error);
    }
  };


  const handleDaySelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleSchedule = (slot: Slot) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }
    
    setSelectedSlot(slot);
    setIsRequestModalOpen(true);
  };

  const handleSubmitRequest = async (message?: string) => {
    if (!selectedSlot || !instructorId || !user) return;

    try {
      await submitRequestMutation.mutateAsync({
        instructor_id: instructorId,
        student_id: user.id,
        requested_date: selectedDate.toISOString().split('T')[0],
        requested_start_time: selectedSlot.start_time,
        requested_end_time: selectedSlot.end_time,
        message
      });

      setIsRequestModalOpen(false);
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error submitting request:', error);
    }
  };

  const handleApproveRequest = async (requestId: string) => {
    if (!instructorId) return;
    
    try {
      await approveRequestMutation.mutateAsync({
        requestId,
        instructorId,
      });
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDenyRequest = async (requestId: string) => {
    if (!instructorId) return;
    
    try {
      await denyRequestMutation.mutateAsync({
        requestId,
        instructorId,
      });
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };

  const toggleSlotExpansion = (slotId: string) => {
    setExpandedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slotId)) {
        newSet.delete(slotId);
      } else {
        newSet.add(slotId);
      }
      return newSet;
    });
  };

  // Group requests by slot and date
  const getRequestsForSlot = (slot: Slot) => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const filteredRequests = instructorRequests.filter(request => 
      request.requested_date === selectedDateStr &&
      normalizeTime(request.requested_start_time) === normalizeTime(slot.start_time)
    );
    
    // Filter out past requests (only for instructor's own profile)
    if (isOwnProfile) {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      return filteredRequests.filter(request => {
        // If request is for today, only show if it's within the last 2 hours
        if (request.requested_date === today) {
          const requestTime = parseInt(request.requested_start_time.split(':')[0]) * 60 + 
                             parseInt(request.requested_start_time.split(':')[1]);
          return requestTime > currentTime - 120; // 2 hours buffer
        }
        
        // If request is for future days, keep it
        return request.requested_date > today;
      });
    }
    
    return filteredRequests;
  };

  const isSlotRequested = (slot: Slot) => {
    if (!user) return false;
    
    const isRequested = studentRequests.some(request => 
      request.instructor_id === instructorId &&
      request.requested_date === selectedDate.toISOString().split('T')[0] &&
      normalizeTime(request.requested_start_time) === normalizeTime(slot.start_time) &&
      request.status === 'pending'
    );
    
    return isRequested;
  };

  const getSlotRequestStatus = (slot: Slot) => {
    if (!user) return null;
    
    const request = studentRequests.find(req => 
      req.instructor_id === instructorId &&
      req.requested_date === selectedDate.toISOString().split('T')[0] &&
      normalizeTime(req.requested_start_time) === normalizeTime(slot.start_time)
    );
    
    return request?.status || null;
  };

  // Função para formatar horário baseado no idioma
  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    
    // Extrair apenas o horário (HH:MM:SS)
    const time = timeString.split('T')[1]?.split('.')[0] || timeString;
    const [hours, minutes] = time.split(':').map(Number);
    
    if (currentLanguage === 'en') {
      // Formato inglês: 12h com AM/PM
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
    } else {
      // Formato português: 24h
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  };

  // Prepare data for SlotsSlider
  const dailySlotCounts = days.map(day => ({
    date: format(day.date, 'yyyy-MM-dd'),
    slot_count: day.slotCount
  }));

  const handleBack = () => {
    navigate(-1);
  };

  if (!instructorId) {
    return (
      <div className="min-h-screen bg-[#160C0C] text-white">
        <TopNavigation />
        <div className="pt-20 pb-20">
          <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
            {/* Empty State Icon */}
            <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
              <Calendar className="w-10 h-10 text-white/40" />
            </div>
            
            {/* Empty State Title */}
            <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
              {t('class_slots.instructor_not_found')}
            </h3>
            
            {/* Empty State Subtitle  */}
            <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
              {t('class_slots.instructor_not_found_subtitle')}
            </p>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <>
      <TopNavigation />
      <div className="relative min-h-screen w-full bg-[#160C0C] pt-20 pb-20">
        <div className="px-4">
          {/* Header */}
          <div className="relative mb-6">
            {/* Back button positioned absolutely */}
            <button
              onClick={handleBack}
              className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2 text-white/60 hover:text-white transition-colors z-10"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">{t('class_slots.back')}</span>
            </button>
            
            {/* Centered title */}
            <div className="flex justify-center">
              <h1 className="text-xl font-semibold text-white">{t('class_slots.title')}</h1>
            </div>
          </div>

          {/* Description and Price */}
          <div className="mb-6">
            <p className="text-white/80 text-xs font-light mb-2">
              {t('class_slots.description')}
            </p>
            <p className="text-white font-medium">{t('class_slots.price')}</p>
          </div>

          {/* Slots Slider - Replacing the old Days Selector */}
          <SlotsSlider
            selectedDate={selectedDate}
            onDateSelect={handleDaySelect}
            dailySlotCounts={dailySlotCounts}
            hideMonthSelector={true}
          />

          {/* Slots List */}
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <p className="text-white/60">{t('class_slots.loading_slots')}</p>
              </div>
            ) : agendaLoading ? (
              <div className="text-center py-8">
                <p className="text-white/60">{t('class_slots.loading_slots')}</p>
              </div>
            ) : !effectiveAgendaConfig ? (
              <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                  <Calendar className="w-10 h-10 text-white/40" />
                </div>
                <h3 className="text-white font-medium text-lg mb-2">
                  No Schedule Available
                </h3>
                <p className="text-white/60 text-sm mb-6">
                  This instructor hasn't set up their schedule yet.
                </p>
              </div>
            ) : (() => {
              // Check if selected date is in the past
              const today = new Date();
              const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              
              if (isPastDate) {
                return (
                  <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                    <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                      <Clock className="w-10 h-10 text-white/40" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                      Past Date
                    </h3>
                    <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                      No slots available for past dates. Please select a current or future date to view available time slots.
                    </p>
                  </div>
                );
              }
              
              // If not past date, check if there are slots
              if (slots.length > 0) {
                return 'hasSlots';
              } else {
                return 'noSlots';
              }
            })() === 'hasSlots' ? (
              slots.map((slot, index) => {
                const slotRequests = getRequestsForSlot(slot);
                const requestCount = slotRequests.length;
                const isOwnProfile = instructorId === user?.id;
                const slotId = slot.slot_id || `slot-${index}`;
                const isExpanded = expandedSlots.has(slotId);
                
                return (
                  <div key={slotId} className="space-y-3">
                    {/* Slot Header */}
                    <div className="bg-[#2d2424] rounded-2xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <p className="text-white font-medium text-[14px]">
                            {formatTime(slot.start_time)} {t('class_slots.to')} {formatTime(slot.end_time)}
                          </p>
                          <p className="text-white/60 text-[12px] font-light">
                            {t('class_slots.private_class')} - {slot.duration_minutes} {t('class_slots.min')}
                          </p>
                        </div>
                        {isOwnProfile ? (
                          // Instructor view - show request count with click to expand
                          requestCount > 0 ? (
                            <button
                              onClick={() => toggleSlotExpansion(slotId)}
                              className="bg-[#d72e16] text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-[#b91c1c] transition-colors"
                            >
                              {requestCount} {requestCount === 1 ? 'request' : 'requests'}
                            </button>
                          ) : (
                            <div className="text-white/60 text-[12px]">
                              0 requests
                            </div>
                          )
                        ) : (
                          // Student view - show schedule button or status
                          (() => {
                            const slotStatus = getSlotRequestStatus(slot);
                            if (slotStatus === 'pending') {
                              return (
                                <div className="bg-yellow-500 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                                  Requested
                                </div>
                              );
                            } else if (slotStatus === 'approved') {
                              return (
                                <div className="bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                                  Approved
                                </div>
                              );
                            } else if (slotStatus === 'denied') {
                              return (
                                <div className="bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                                  Denied
                                </div>
                              );
                            } else {
                              return (
                                <button
                                  onClick={() => handleSchedule(slot)}
                                  className="border border-white text-white text-[10px] font-bold px-3 py-1 rounded-full hover:bg-white/10 transition-colors"
                                >
                                  {t('class_slots.schedule')}
                                </button>
                              );
                            }
                          })()
                        )}
                      </div>
                    </div>

                    {/* Individual Request Cards - Only show when expanded and for instructor's own profile */}
                    {isOwnProfile && isExpanded && slotRequests.length > 0 && (
                      <div className="space-y-2 ml-4">
                        {slotRequests.map((request) => (
                          <IndividualRequestCard
                            key={request.request_id}
                            request={request}
                            onAccept={handleApproveRequest}
                            onDeny={handleDenyRequest}
                            isAccepting={approveRequestMutation.isPending}
                            isDenying={denyRequestMutation.isPending}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
                {/* Empty State Icon */}
                <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                  <Calendar className="w-10 h-10 text-white/40" />
                </div>
                
                {/* Empty State Title */}
                <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                  {(() => {
                    const now = new Date();
                    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
                    return isToday ? t('class_slots.no_more_slots_today') : t('class_slots.no_slots_available');
                  })()}
                </h3>
                
                {/* Empty State Subtitle */}
                <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                  {(() => {
                    const now = new Date();
                    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
                    return isToday 
                      ? t('class_slots.no_more_slots_today_subtitle')
                      : t('class_slots.no_slots_subtitle');
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomNavigation />

      {/* Class Request Modal */}
      {selectedSlot && (
        <ClassRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => {
            setIsRequestModalOpen(false);
            setSelectedSlot(null);
          }}
          slot={selectedSlot}
          instructorId={instructorId || ''}
          instructorName={instructorName}
          instructorAvatar={instructorAvatar}
          instructorBeltRank={instructorBeltRank}
          selectedDate={selectedDate}
          onSubmit={handleSubmitRequest}
          isLoading={submitRequestMutation.isPending}
        />
      )}
    </>
  );
};
